import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useWatch, Controller } from 'react-hook-form';
import {
  DashboardProspectsFormProps,
  DashboardProspectsFormSchema,
  DashboardProspectsMutation,
  UpdateStatusProspectWithCommentQuery,
} from './UpdateStatusProspectForm.types';
import { useMutation, useQueryClient } from 'react-query';
import { getUpdateStatusProspectSchema } from './UpdateStatusProspectForm.schema';
import { useNavigate } from 'react-router-dom';
import {
  UPDATE_PROSPECT_STATUS_WITH_COMMENT_QUERY_KEY,
  updateStatusProspectWithCommentQuery,
} from './UpdateStatusProspectForm.query';
import { updateStatusProspectFormData } from './UpdateStatusProspectForm.data';
import { useEffect, useMemo } from 'react';
import Select from '../../../../../../../commons/Select/Select';
import { SelectOption } from '../../../../../../../commons/Select/Select.types';
import Button from '../../../../../../../designSystem/Button/Button';
import { ButtonSize } from '../../../../../../../designSystem/Button/Button.types';
import TextArea from '../../../../../../../commons/TextArea/TextArea';
import Calendar from 'react-calendar';
import moment from 'moment';
import { ProspectStatus } from '../../../../../../../../types/api/endpoints/prospect/prospect.types';

export default ({ prospect }: DashboardProspectsFormProps) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const formProps = useForm<DashboardProspectsFormSchema>({
    resolver: yupResolver(getUpdateStatusProspectSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = formProps;

  const status = useWatch({
    control,
    name: 'status',
  });

  useEffect(() => setValue('status', prospect.status, { shouldValidate: true }), []);
  useEffect(() => console.log('status', status), [status]);

  const { mutate } = useMutation<
    Awaited<ReturnType<DashboardProspectsMutation>>,
    Error,
    UpdateStatusProspectWithCommentQuery
  >((data) => updateStatusProspectWithCommentQuery(data), {
    mutationKey: UPDATE_PROSPECT_STATUS_WITH_COMMENT_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      navigate(0);
    },
    onError: (error) => {
      alert(error.message);
    },
  });

  const onDateChange = (date: Date) => {
    setValue('reminder_date', moment(date).format('YYYY-MM-DD'), { shouldValidate: true });
  };

  const onSubmit = handleSubmit((data) =>
    mutate({
      ...data,
      id: prospect.id,
      email: prospect.email,
      reminder_date: status === ProspectStatus.Relancer ? data.reminder_date : undefined,
    })
  );

  const updatedFormData = useMemo(
    () =>
      updateStatusProspectFormData.map((row) =>
        row.map((item) => {
          switch (item.name) {
            case 'status':
              return {
                ...item,
                defaultValue: [
                  {
                    value: prospect.status,
                    label: prospect.status,
                  },
                ],
              };
            default:
              return item;
          }
        })
      ),
    []
  );

  return (
    <form className="form" style={{ width: 'calc(100% - var(--spacing-mToL) * 2)' }}>
      {updatedFormData.map((row, rowIndex) => (
        <div
          key={`row#${rowIndex}`}
          className="form__row"
          style={{ textTransform: 'capitalize', alignSelf: rowIndex === 2 ? 'center' : 'unset' }}>
          {row.map(({ name, placeholder, label, options, ...props }, inputIndex) => (
            <Controller
              key={`controlledInput#${inputIndex}`}
              name={name}
              render={({ field: { onChange } }) => {
                switch (name) {
                  case 'status':
                    return (
                      <Select
                        placeholder={placeholder || ''}
                        error={errors[name]?.message as string}
                        {...{ label, options }}
                        {...props}
                        // @ts-ignore
                        onChange={(arr: SelectOption | SelectOption[]) => {
                          if ((arr as SelectOption[]).length) {
                            const values = (arr as SelectOption[]).map((item) => {
                              if (typeof item === 'string') {
                                return item;
                              } else {
                                return item.value;
                              }
                            });
                            onChange(values);
                          } else {
                            onChange((arr as SelectOption).value);
                          }
                        }}
                      />
                    );
                  case 'comment':
                    return (
                      // @ts-ignore
                      <TextArea
                        className="dashboard-history__question-textarea"
                        error={errors[name]?.message as string}
                        // onPressEnter={onSubmit}
                        {...{ name, label, placeholder, onChange }}
                        {...props}
                      />
                    );
                  case 'reminder_date':
                    return (
                      <>
                        <input type="hidden" />
                        {status === ProspectStatus.Relancer && (
                          <>
                            <label className="input__label font-bold">
                              Date de rappel <span style={{ color: 'red' }}>*</span>
                            </label>
                            <Calendar
                              // @ts-ignore
                              onChange={onDateChange}
                              className={'react-calendar--centered'}
                            />
                          </>
                        )}
                      </>
                    );
                }
              }}
              {...{ control }}
            />
          ))}
        </div>
      ))}
      <Button
        style={{ alignSelf: 'unset' }}
        onClick={(e) => {
          e.preventDefault();
          // setPaymentModalOpen(true);
          onSubmit();
        }}
        size={ButtonSize.Large}
        disabled={!isValid}>
        Valider
      </Button>
    </form>
  );
};
